// Platforms (spec/dashboard.md section 9.11, R-PAY-01; spec/api-conventions.md section 15.4): one card per Nutaku slot of
// the environment — Title id, Application id, the write-only S2S key (whether one is set and its hint, a "New key" field
// sent once), Enabled, the handshake and payment handler URLs with Copy. Edits stage `platform-config` drafts (id
// `<env slug>/<platform>`) that Save All PUTs; Remove is a command (confirm → DELETE).
import { useParams } from 'react-router';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { Json } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Badge, draftTone, draftWord } from '../../ui/Chip';
import { useConfirm } from '../../ui/ConfirmDialog';
import { EmptyState } from '../../ui/Banner';
import { Field } from '../../ui/Field';
import { CommitTextInput, Switch } from '../../ui/inputs';
import { useToasts } from '../../ui/Toast';
import { queryCache, useQuery } from '../../workspace/cache';
import { useWorkspace, useWorkspaceVersion } from '../../workspace/react';
import { entityKey, type Draft, type Workspace } from '../../workspace/Workspace';
import { NUTAKU_SLOTS, type PlatformSlot } from './slots';
import { useEnvironmentsRefresh } from './useEnvironmentsRefresh';
import './env.css';
import { LoadError, LoadingPane } from '../../ui/PageStates';
import { AlertCircle, Copy, Plus } from '../../ui/icons';

/** The wire record (PlatformConfigDto); the S2S key itself is never part of it. */
interface PlatformRecord {
  id: string;
  environmentId: string;
  platform: string;
  code: number | string;
  titleId: string | null;
  appId: string | null;
  hasS2sKey: boolean;
  s2sKeyHint: string | null;
  enabled: boolean;
  handshakeUrl: string;
  paymentHandlerUrl: string;
  version: number | string;
}

/** What a draft holds: the record's editable fields, plus `s2sKey` only while a new key is staged. */
interface PlatformValue {
  platform: string;
  titleId?: string | null;
  appId?: string | null;
  s2sKey?: string | null;
  enabled?: boolean;
}

export function PlatformsPage() {
  const { env: envSlug = '' } = useParams();
  const { org, game, environments, can } = useGame();
  const { client } = useAuth();
  const workspace = useWorkspace();
  useWorkspaceVersion(workspace);
  const env = environments.find((e) => e.slug === envSlug);
  const canWrite = can('manage_environment', envSlug);
  const path = { org, game: game.slug, env: envSlug };
  // keyed under the environment workspace's prefix, so the shell's Save All (which invalidates `branch:<game>:env:<slug>`) re-reads it
  const listKey = `branch:${game.id}:env:${envSlug}:platforms`;
  const recordsQ = useQuery(env ? listKey : null, () => client.data(ops.listPlatformConfigs, { path }));
  const records = ((recordsQ.data as { items?: PlatformRecord[] } | undefined)?.items ?? []) as PlatformRecord[];
  const reload = () => queryCache.invalidate(listKey);

  useEnvironmentsRefresh(workspace, org, game.slug);

  if (!env) {
    return (
      <section className="env-page">
        <h1>Platforms</h1>
        <EmptyState title={`No environment “${envSlug}”`} body="Pick an environment with the chips of the branch selector." />
      </section>
    );
  }

  const error = recordsQ.error instanceof ApiError ? recordsQ.error : recordsQ.error ? new ApiError(0, 'unavailable', String(recordsQ.error), '') : null;
  const extra = records.filter((r) => !NUTAKU_SLOTS.some((s) => s.slug === r.platform));
  return (
    <section className="env-page" data-testid="platforms">
      <div className="page-head">
        <h1>Platforms</h1>
        <Badge tone={env.kind === 'prod' ? 'danger' : env.kind === 'stage' ? 'warning' : 'info'}>{env.kind}</Badge>
        <span className="page-meta">
          {env.name} <code>{env.slug}</code>
        </span>
        <span className="spacer" />
        <span className="page-sub">Edits are staged; Save All sends them.</span>
      </div>
      <p className="muted env-note">
        The Nutaku platform records of <strong>{env.name}</strong>. They are per environment: configure the sandbox slots on dev and the live ones on prod, each with the S2S key Nutaku sends and the two URLs to enter in the Nutaku console.
      </p>
      {error && (
        <div className="banner banner-danger" role="alert">
          <AlertCircle className="banner-icon" aria-hidden="true" />
          <div className="banner-body">
            {error.message}
            {error.requestId ? ` (request ${error.requestId})` : ''}
          </div>
          <Button size="s" onClick={reload}>
            Retry
          </Button>
        </div>
      )}
      {recordsQ.loading && !recordsQ.data ? (
        <LoadingPane rows={4} label="Loading the platforms" />
      ) : (
        <div className="platform-cards">
          {NUTAKU_SLOTS.map((slot) => (
            <PlatformCard key={slot.slug} slot={slot} envSlug={env.slug} record={records.find((r) => r.platform === slot.slug)} workspace={workspace} canWrite={canWrite} onRemoved={reload} />
          ))}
          {extra.map((r) => (
            <PlatformCard key={r.platform} slot={{ slug: r.platform, code: Number(r.code), label: r.platform }} envSlug={env.slug} record={r} workspace={workspace} canWrite={canWrite} onRemoved={reload} />
          ))}
        </div>
      )}
    </section>
  );
}

function PlatformCard({ slot, envSlug, record, workspace, canWrite, onRemoved }: { slot: PlatformSlot; envSlug: string; record: PlatformRecord | undefined; workspace: Workspace; canWrite: boolean; onRemoved: () => void }) {
  const { org, game } = useGame();
  const { client } = useAuth();
  const confirm = useConfirm();
  const toasts = useToasts();
  const id = `${envSlug}/${slot.slug}`;
  const key = entityKey('platform-config', id);
  const draft: Draft | undefined = workspace.getDraft(key);
  const value = (draft?.current as unknown as PlatformValue | undefined) ?? (record as unknown as PlatformValue | undefined);
  const meta = { label: slot.slug, template: 'Platforms' };

  const edit = (field: 'titleId' | 'appId' | 's2sKey' | 'enabled', v: unknown) => {
    workspace.edit('platform-config', id, field, v, { base: record ? { version: Number(record.version), value: record as unknown as Json } : undefined, ...meta });
  };
  const configure = () => {
    workspace.create('platform-config', { platform: slot.slug, titleId: '', appId: '', s2sKey: '', enabled: true } as unknown as Json, { tempId: id, ...meta });
  };
  const copy = async (what: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toasts.success(`${what} copied.`);
    } catch {
      toasts.error(`Could not copy the ${what.toLowerCase()}; select it and copy it by hand.`);
    }
  };
  const removeRecord = async () => {
    if (!record) return;
    const ok = await confirm({
      title: `Remove ${slot.slug}?`,
      body: `The ${slot.label} record of this environment is deleted now (a command, not a staged change). Nutaku calls for this slot fail until it is configured again.`,
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await client.call(ops.deletePlatformConfig, { path: { org, game: game.slug, env: envSlug, platform: slot.slug }, ifMatch: Number(record.version) });
      workspace.discardEntity(key);
      toasts.success(`${slot.slug} removed.`);
      onRemoved();
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError(0, 'unavailable', String(e), '');
      toasts.error(err.status === 412 ? 'The record changed on the server since; reload the page and try again.' : err.message, { requestId: err.requestId });
    }
  };

  const status = draft?.status;
  const problem = draft?.conflict?.message ?? draft?.error?.message ?? null;
  const stagedKey = typeof value?.s2sKey === 'string' && value.s2sKey !== '';
  return (
    <article className={`pane platform-card ${value ? '' : 'platform-unconfigured'}`} data-testid={`platform-${slot.slug}`} aria-label={slot.slug}>
      <header className="pane-head">
        <h2 className="platform-title">{slot.label}</h2>
        <code className="small">{slot.slug}</code>
        <Badge title="The legacy platform code (the SDK's and the game API's `?platform=`)">code {slot.code}</Badge>
        {status && <Badge tone={draftTone[status]}>{draftWord[status]}</Badge>}
        {value && <span className={`status-dot ${value.enabled === false ? 'status-off' : 'status-live'}`}>{value.enabled === false ? 'off' : 'on'}</span>}
        <span className="spacer" />
        {record && (
          <Button size="s" variant="danger" onClick={() => void removeRecord()} missingPermission={canWrite ? null : 'manage_environment'} aria-label={`Remove ${slot.slug}`}>
            Remove
          </Button>
        )}
      </header>
      <div className="pane-body">
      {problem && (
        <LoadError>{problem}</LoadError>
      )}
      {!value ? (
        <p className="row platform-empty">
          <span className="muted">Not configured: Nutaku calls for this slot fail.</span>
          <Button size="s" icon={<Plus aria-hidden="true" />} onClick={configure} aria-label={`Configure ${slot.slug}`} missingPermission={canWrite ? null : 'manage_environment'}>
            Configure
          </Button>
        </p>
      ) : (
        <div className="form-grid platform-fields">
          <Field label="Title id">
            <CommitTextInput value={value.titleId ?? ''} onCommit={(v) => edit('titleId', v)} disabled={!canWrite} aria-label={`Title id for ${slot.slug}`} />
          </Field>
          <Field label="Application id">
            <CommitTextInput value={value.appId ?? ''} onCommit={(v) => edit('appId', v)} disabled={!canWrite} aria-label={`Application id for ${slot.slug}`} />
          </Field>
          <Field label="S2S key" help="Write-only: sent once with Save All and never read back; the server keeps a hash and the last four characters.">
            <span className="stack">
              <span className="small" data-testid={`s2s-status-${slot.slug}`}>
                {record?.hasS2sKey ? `Key set (…${record.s2sKeyHint ?? ''})` : 'No key'}
                {stagedKey ? ' · a new key is staged' : ''}
              </span>
              <CommitTextInput
                type="password"
                autoComplete="new-password"
                placeholder="New key"
                value={typeof value.s2sKey === 'string' ? value.s2sKey : ''}
                onCommit={(v) => edit('s2sKey', v)}
                disabled={!canWrite}
                aria-label={`New S2S key for ${slot.slug}`}
              />
            </span>
          </Field>
          <Field label="Enabled">
            <Switch label={value.enabled === false ? 'Disabled' : 'Enabled'} aria-label={`Enabled ${slot.slug}`} checked={value.enabled !== false} onChange={(v) => edit('enabled', v)} disabled={!canWrite} />
          </Field>
          <Field label="Handshake URL" className="span-2">
            {record ? (
              <span className="row platform-url">
                <code className="small">{record.handshakeUrl}</code>
                <Button size="s" variant="ghost" icon={<Copy aria-hidden="true" />} onClick={() => void copy('Handshake URL', record.handshakeUrl)} aria-label="Copy handshake URL">
                  Copy
                </Button>
              </span>
            ) : (
              <span className="muted small">Shown once the record is saved.</span>
            )}
          </Field>
          <Field label="Payment handler URL" className="span-2">
            {record ? (
              <span className="row platform-url">
                <code className="small">{record.paymentHandlerUrl}</code>
                <Button size="s" variant="ghost" icon={<Copy aria-hidden="true" />} onClick={() => void copy('Payment handler URL', record.paymentHandlerUrl)} aria-label="Copy payment handler URL">
                  Copy
                </Button>
              </span>
            ) : (
              <span className="muted small">Shown once the record is saved.</span>
            )}
          </Field>
          {status === 'new' && (
            <p className="row span-2 platform-new">
              <Button size="s" variant="ghost" onClick={() => workspace.discardEntity(key)}>
                Discard
              </Button>
              <span className="muted small">Not saved yet: Save All creates the record.</span>
            </p>
          )}
        </div>
      )}
      </div>
    </article>
  );
}
